/**
 * Shared utility functions for form processing across PatientPage, EditableDocumentPage, and SchedulesTable.
 * This ensures consistent behavior for the "Process form" functionality.
 */

import { getSupabase } from "../components/supabaseClient";
import {
  bucketName_files,
  prompts,
  getTypeFieldIndex,
} from "../components/env";

// Get bucket name with fallback
const getBucketName = () => {
  return bucketName_files &&
    bucketName_files !== "undefined" &&
    bucketName_files !== undefined
    ? bucketName_files
    : "files";
};

/**
 * Get the current user's name from the session
 */
export const getCurrentUserName = async () => {
  try {
    const supabase = await getSupabase();
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session?.user?.id) {
      return null;
    }

    // Fetch user name from users table
    const { data, error } = await supabase
      .from("users")
      .select("name")
      .eq("id", session.user.id)
      .single();

    if (error || !data?.name) {
      // Fallback to email if name not found
      return session?.user?.email || null;
    }

    return data.name;
  } catch (error) {
    console.error("❌ formProcessingUtils: Error getting user name:", error);
    try {
      const supabase = await getSupabase();
      const {
        data: { session },
      } = await supabase.auth.getSession();
      return session?.user?.email || null;
    } catch (e) {
      return null;
    }
  }
};

/**
 * Get template type prefix based on patient type
 */
export const getTemplateTypePrefix = (patientType) => {
  if (patientType === "followup") return "followup";
  return patientType === "NP" || patientType === "FFP" ? "np" : "ns";
};

/**
 * Get template folder name based on patient type
 * followup templates go to followup_templates, others go to forms_templates
 */
export const getTemplateFolderName = (patientType) => {
  if (patientType === "followup") return "followup_templates";
  return "forms_templates";
};

/**
 * Check if a form name is a qualifying questionnaire
 * Uses keyword-based matching to handle typos and variations in form names
 */
export const isQualifyingFormName = (formName) => {
  if (!formName) return false;

  const lowerName = formName.toLowerCase();

  // Check for Sleep questionnaire keywords
  const isSleepForm =
    lowerName.includes("sleep") &&
    (lowerName.includes("new") ||
      lowerName.includes("patient") ||
      lowerName.includes("question"));

  // Check for Pulmonary questionnaire keywords
  const isPulmonaryForm =
    lowerName.includes("pulmonary") &&
    (lowerName.includes("new") ||
      lowerName.includes("patient") ||
      lowerName.includes("question"));

  return isSleepForm || isPulmonaryForm;
};

/**
 * Get the NS (New Sleep) template
 */
export const getNSTemplate = () => {
  return `HPI: __ year old [gender] presents to our clinic initially on -_--, for evaluation of sleep concerns.
Patient has been snoring for Patient complains of dry mouth, sleep-related leg movements, lack of feeling rested after waking up, and memory/concentration issues. He is not getting enough oxygen to his brain.
Patient has not had: CPAP, GGA, MMA and UPPP.

Sleep ROS:
Denies any morning headaches, lack of feeling rested after waking up, memory/concentration issues, sleep paralysis, hypnagogic/hypnopompic hallucinations, sleep-related leg movements, bruxism, sleep walking, sleep talking.

Review of Systems: As per HPI, all other 12 ROS were reviewed and otherwise NEGATIVE.

Past Medical History:
Past Surgical History:
Family History:
Social History:
Allergies:
Meds:

Physical Exam:
General: NAD.
HEENT: NCAT.
Respiratory: CTAB, no W/R/R
Cardiac: S1S2 appreciated

# Snoring
# OSA
# Witnessed Apneic Episode

# RHM (routine health maintenance)
-Next follow-up:
-Medications ordered:
-Contact Info:`;
};

/**
 * Get the NP (New Pulmonary) template
 */
export const getNPTemplate = () => {
  return `HPI: __ year old [gender] presents to our clinic initially on _____, for evaluation of ___.

Heart and lung sounds upon auscultation: ___

Review of Systems: As per HPI, all other 12 ROS were reviewed and otherwise NEGATIVE.

Past Medical History:
Past Surgical History:
Family History:
Social History:
Allergies:
Meds:

Physical Exam:
General: NAD.
HEENT: NCAT.
Respiratory: CTAB, no W/R/R
Cardiac: S1S2 appreciated

#

# RHM (routine health maintenance)
-Next follow-up:
-Medications ordered:
-Contact Info:`;
};

/**
 * Get the default prompt text
 */
export const getDefaultPrompt = (templateTypeName) => {
  return `Only use the form info and run the following:

Please fill the template below with information from the forms. Only use information from the form & attached images.

Add assessment and plan section at the bottom. Use the exact ${templateTypeName} template format below and structure.

Output using consistent markdown formatting: use bold for all section headers and field names (e.g. HPI:, Current Weight:, Sleep ROS:). Use bullet lists (e.g. - Cigarette smoking history:) where the template has list items. Keep the assessment items as a clear list (e.g. - Snoring, - OSA). Do not output plain text without this formatting.`;
};

/**
 * Load user's personal default template and prompt
 */
export const loadUserDefaultTemplate = async (
  patientType,
  templateTypeName,
) => {
  try {
    const userName = await getCurrentUserName();
    if (!userName) return { templateText: null, promptText: null };

    const supabase = await getSupabase();
    const BUCKET_NAME = getBucketName();
    const templatePrefix = getTemplateTypePrefix(patientType);
    const sanitizedUserName = userName.replace(/[^a-zA-Z0-9@._-]/g, "_");
    const templateFolderName = getTemplateFolderName(patientType);
    const userFolder = `New/templates/${templateFolderName}/${sanitizedUserName}`;

    const templatePath = `${userFolder}/default_template_${templatePrefix}`;
    const promptPath = `${userFolder}/default_prompt_${templatePrefix}`;

    let templateText = null;
    let promptText = null;

    const [templateResult, promptResult] = await Promise.all([
      supabase.storage.from(BUCKET_NAME).download(templatePath),
      supabase.storage.from(BUCKET_NAME).download(promptPath),
    ]);

    if (!templateResult.error && templateResult.data) {
      templateText = await templateResult.data.text();
    }
    if (!promptResult.error && promptResult.data) {
      promptText = await promptResult.data.text();
      promptText = promptText.replace(
        /\$\{templateTypeName\}/g,
        templateTypeName,
      );
    }

    if (templateText && promptText) {
      console.log(
        `✅ formProcessingUtils: Loaded user default template for ${patientType}`,
      );
    }

    return { templateText, promptText };
  } catch (error) {
    console.warn(
      "⚠️ formProcessingUtils: Error loading user default template:",
      error,
    );
    return { templateText: null, promptText: null };
  }
};

/**
 * Load template and prompt from database (checks user default first, then global)
 */
export const loadTemplateAndPrompt = async (patientType, templateTypeName) => {
  // First check for user's personal default
  const userDefault = await loadUserDefaultTemplate(
    patientType,
    templateTypeName,
  );
  if (userDefault.templateText && userDefault.promptText) {
    return userDefault;
  }

  const supabase = await getSupabase();
  const BUCKET_NAME = getBucketName();

  let templateText = null;
  let promptText = null;

  // Determine which template folder and file to load based on patient type
  const templateFolderName = getTemplateFolderName(patientType);
  const templatePrefix = getTemplateTypePrefix(patientType);
  const templateFileName = `New/templates/${templateFolderName}/default_template_${templatePrefix}`;

  try {
    const { data: templateData, error: templateError } = await supabase.storage
      .from(BUCKET_NAME)
      .download(templateFileName);

    if (!templateError && templateData) {
      templateText = await templateData.text();
      console.log(
        `✅ formProcessingUtils: Loaded ${patientType} template from database`,
      );
    }
  } catch (error) {
    console.error("❌ formProcessingUtils: Error loading template:", error);
  }

  try {
    const promptPath = `New/templates/${templateFolderName}/default_prompt`;
    const { data: promptData, error: promptError } = await supabase.storage
      .from(BUCKET_NAME)
      .download(promptPath);

    if (!promptError && promptData) {
      promptText = await promptData.text();
      promptText = promptText.replace(
        /\$\{templateTypeName\}/g,
        templateTypeName,
      );
      console.log("✅ formProcessingUtils: Loaded prompt from database");
    }
  } catch (error) {
    console.error("❌ formProcessingUtils: Error loading prompt:", error);
  }

  return { templateText, promptText };
};

/**
 * Load saved user templates for a specific patient type
 */
export const loadSavedUserTemplates = async (patientType) => {
  try {
    const userName = await getCurrentUserName();
    if (!userName) {
      console.warn(
        "⚠️ formProcessingUtils: No user name, cannot load saved templates",
      );
      return [];
    }

    const supabase = await getSupabase();
    const BUCKET_NAME = getBucketName();
    const templatePrefix = getTemplateTypePrefix(patientType);
    const templateFolderName = getTemplateFolderName(patientType);
    const sanitizedUserName = userName.replace(/[^a-zA-Z0-9@._-]/g, "_");
    const userFolder = `New/templates/${templateFolderName}/${sanitizedUserName}`;

    // List all files in user folder
    const { data: files, error } = await supabase.storage
      .from(BUCKET_NAME)
      .list(userFolder);

    if (error) {
      console.warn(
        "⚠️ formProcessingUtils: Error listing user templates:",
        error,
      );
      return [];
    }

    // Filter for this template type - look for template_ prefix files (not prompt_ or name_)
    const templateFiles = (files || [])
      .filter(
        (file) =>
          file.name.startsWith("template_") &&
          !file.name.startsWith("prompt_") &&
          !file.name.startsWith("name_"),
      )
      .map((file) => file.name.replace(/^template_/, ""))
      .filter((name) => name.length > 0);

    // Load each template/prompt pair (name is stored in template file header)
    const savedTemplates = [];
    for (const fileName of templateFiles) {
      try {
        const templatePath = `${userFolder}/template_${fileName}`;
        const promptPath = `${userFolder}/prompt_${fileName}`;

        const [templateResult, promptResult] = await Promise.all([
          supabase.storage.from(BUCKET_NAME).download(templatePath),
          supabase.storage.from(BUCKET_NAME).download(promptPath),
        ]);

        if (!templateResult.error && !promptResult.error) {
          const templateContent = await templateResult.data.text();
          const promptText = await promptResult.data.text();

          // Extract name from template file header (format: "TEMPLATE_NAME: name\nAUTHOR: author\nIMPORTED_FROM: shared\n---\ncontent")
          let templateName = fileName;
          let templateText = templateContent;
          if (templateContent.startsWith("TEMPLATE_NAME:")) {
            const headerEnd = templateContent.indexOf("\n---");
            if (headerEnd !== -1) {
              const headerContent = templateContent.substring(0, headerEnd);
              // Extract just the template name value after "TEMPLATE_NAME: "
              const nameMatch = headerContent.match(/^TEMPLATE_NAME:\s*(.+)$/m);
              if (nameMatch) {
                templateName = nameMatch[1].trim();
              }
              // Get actual template text (after ---)
              templateText = templateContent.substring(headerEnd + 4).trim();
              if (templateText.startsWith("\n")) {
                templateText = templateText.substring(1);
              }
            }
          }
          savedTemplates.push({
            fileName: fileName,
            template: templateText,
            prompt: promptText,
            name: templateName,
          });
        }
      } catch (error) {
        console.warn(
          `⚠️ formProcessingUtils: Error loading template ${fileName}:`,
          error,
        );
      }
    }

    console.log(
      `✅ formProcessingUtils: Loaded ${savedTemplates.length} saved templates for ${patientType}`,
    );
    return savedTemplates;
  } catch (error) {
    console.error(
      "❌ formProcessingUtils: Error loading saved templates:",
      error,
    );
    return [];
  }
};

/**
 * Check if patient's forms have full_text in the database
 */
export const checkFormsFullTextStatus = async (submissions_array) => {
  if (!Array.isArray(submissions_array) || submissions_array.length === 0) {
    return { hasFullText: false, hasNullFullText: false };
  }

  const supabase = await getSupabase();
  let hasFullText = false;
  let hasNullFullText = false;

  for (const submission of submissions_array) {
    let form_id = null;
    let submission_id = null;

    if (typeof submission === "object" && submission !== null) {
      form_id = submission.form_id;
      submission_id = submission.submission_id;

      if (!form_id || !submission_id) {
        const url = submission.url || submission;
        if (typeof url === "string") {
          const match = url.match(/\/([^/]+)\/submission\/([^/]+)/);
          if (match) {
            form_id = match[1];
            submission_id = match[2];
          }
        }
      }
    } else if (typeof submission === "string") {
      const match = submission.match(/\/([^/]+)\/submission\/([^/]+)/);
      if (match) {
        form_id = match[1];
        submission_id = match[2];
      }
    }

    if (form_id && submission_id) {
      const { data: formData, error: formError } = await supabase
        .from("StoringFormSubmission")
        .select("full_text")
        .eq("form_id", form_id)
        .eq("submission_id", submission_id)
        .maybeSingle();

      if (!formError && formData) {
        if (formData.full_text) {
          hasFullText = true;
        } else {
          hasNullFullText = true;
        }
      } else {
        hasNullFullText = true;
      }
    }
  }

  return { hasFullText, hasNullFullText };
};

/**
 * Get patient type from patient data
 */
export const getPatientType = (patientData) => {
  if (!patientData) return null;
  return patientData[prompts[getTypeFieldIndex()][0]] || patientData.type;
};

/**
 * Check if patient type is valid for form processing
 */
export const isValidPatientType = (patientType) => {
  return (
    patientType === "NP" ||
    patientType === "NS" ||
    patientType === "FFP" ||
    patientType === "FFS"
  );
};

/**
 * Get template type name for display
 */
export const getTemplateTypeName = (patientType) => {
  if (patientType === "NP") return "New Pulmonary (NP)";
  if (patientType === "FFP") return "Follow-up Pulmonary (FFP)";
  if (patientType === "NS") return "New Sleep (NS)";
  if (patientType === "FFS") return "Follow-up Sleep (FFS)";
  return "New Pulmonary (NP)"; // fallback for non-standard types
};

/**
 * Prepare form processing data (template, prompt, full text status)
 * Returns all the data needed to populate the prompt editor modal
 */
export const prepareFormProcessingData = async (patientData) => {
  const patientType = getPatientType(patientData);

  const templateTypeName = getTemplateTypeName(patientType);

  // Load template and prompt from database
  const { templateText: dbTemplate, promptText: dbPrompt } =
    await loadTemplateAndPrompt(patientType, templateTypeName);

  // Build the template info
  let templateInfoText;
  if (dbTemplate) {
    templateInfoText = dbTemplate;
  } else if (patientType === "NS" || patientType === "FFS") {
    templateInfoText = getNSTemplate();
  } else {
    templateInfoText = getNPTemplate();
  }

  // Build the default editable prompt
  let defaultEditablePrompt;
  if (dbPrompt) {
    defaultEditablePrompt = dbPrompt;
  } else {
    defaultEditablePrompt = getDefaultPrompt(templateTypeName);
  }

  // Check full_text status
  const submissions_array = patientData?.submissions_array || [];
  const { hasFullText, hasNullFullText } =
    await checkFormsFullTextStatus(submissions_array);

  // Load saved user templates
  const savedTemplates = await loadSavedUserTemplates(patientType);

  return {
    patientType,
    templateTypeName,
    templateInfo: templateInfoText,
    editablePrompt: defaultEditablePrompt,
    useStoredFullText: hasFullText && !hasNullFullText,
    forceProcessForms: !hasFullText && hasNullFullText,
    savedTemplates,
  };
};

/**
 * Sanitize a template name for use as a file name
 */
const sanitizeFileName = (name) => {
  if (!name || !name.trim()) return null;
  // Replace spaces and special characters with underscores
  return name.trim().replace(/[^a-zA-Z0-9@._-]/g, "_");
};

/**
 * Save a new user template with custom name
 */
export const saveUserTemplate = async (
  patientType,
  templateText,
  promptText,
  templateName = null,
) => {
  const userName = await getCurrentUserName();
  if (!userName) throw new Error("No user name available");

  const supabase = await getSupabase();
  const BUCKET_NAME = getBucketName();
  const templatePrefix = getTemplateTypePrefix(patientType);
  const templateFolderName = getTemplateFolderName(patientType);
  const sanitizedUserName = userName.replace(/[^a-zA-Z0-9@._-]/g, "_");
  const userFolder = `New/templates/${templateFolderName}/${sanitizedUserName}`;

  // Use custom name or generate default
  const finalName =
    templateName && templateName.trim() ? templateName.trim() : `My Template`;

  // Sanitize the name for use as file name
  const sanitizedFileName = sanitizeFileName(finalName);
  if (!sanitizedFileName) {
    throw new Error("Invalid template name");
  }

  // Check if a template with this name already exists
  const savedTemplates = await loadSavedUserTemplates(patientType);
  const existingTemplate = savedTemplates.find((t) => t.name === finalName);

  if (existingTemplate) {
    throw new Error(
      `A template named "${finalName}" already exists. Please use a different name or update the existing template.`,
    );
  }

  // Store name in template file as header (format: "TEMPLATE_NAME: name\n---\ncontent")
  const templateWithHeader = `TEMPLATE_NAME: ${finalName}\n---\n${templateText}`;
  const templatePath = `${userFolder}/template_${sanitizedFileName}`;
  const promptPath = `${userFolder}/prompt_${sanitizedFileName}`;

  const [templateResult, promptResult] = await Promise.all([
    supabase.storage
      .from(BUCKET_NAME)
      .upload(
        templatePath,
        new Blob([templateWithHeader], { type: "text/plain" }),
        {
          upsert: true,
          contentType: "text/plain",
        },
      ),
    supabase.storage
      .from(BUCKET_NAME)
      .upload(promptPath, new Blob([promptText], { type: "text/plain" }), {
        upsert: true,
        contentType: "text/plain",
      }),
  ]);

  if (templateResult.error || promptResult.error) {
    throw new Error(
      templateResult.error?.message ||
        promptResult.error?.message ||
        "Upload failed",
    );
  }

  console.log(`✅ formProcessingUtils: Saved template "${finalName}"`);
  return { name: finalName, fileName: sanitizedFileName };
};

/**
 * Update an existing user template by file name
 */
export const updateUserTemplate = async (
  patientType,
  templateText,
  promptText,
  templateFileName,
  templateName = null,
) => {
  const userName = await getCurrentUserName();
  if (!userName) throw new Error("No user name available");

  const supabase = await getSupabase();
  const BUCKET_NAME = getBucketName();
  const templatePrefix = getTemplateTypePrefix(patientType);
  const templateFolderName = getTemplateFolderName(patientType);
  const sanitizedUserName = userName.replace(/[^a-zA-Z0-9@._-]/g, "_");
  const userFolder = `New/templates/${templateFolderName}/${sanitizedUserName}`;

  if (!templateFileName) {
    throw new Error("Template file name is required");
  }

  const templatePath = `${userFolder}/template_${templateFileName}`;
  const promptPath = `${userFolder}/prompt_${templateFileName}`;

  // Use provided name, or extract from existing template file to preserve it
  let existingName = templateFileName;
  if (!templateName) {
    try {
      const { data: existingTemplate } = await supabase.storage
        .from(BUCKET_NAME)
        .download(templatePath);
      if (existingTemplate) {
        const content = await existingTemplate.text();
        if (content.startsWith("TEMPLATE_NAME:")) {
          const headerEnd = content.indexOf("\n---");
          if (headerEnd !== -1) {
            // Start after "TEMPLATE_NAME: " (14 characters)
            const headerLine = content.substring(14, headerEnd).trim();
            if (headerLine) {
              existingName = headerLine;
            }
          }
        }
      }
    } catch (e) {
      console.log(
        "Note: Could not read existing template for name preservation",
      );
    }
  } else {
    existingName = templateName;
  }

  // Remove existing files first
  try {
    await Promise.allSettled([
      supabase.storage.from(BUCKET_NAME).remove([templatePath]),
      supabase.storage.from(BUCKET_NAME).remove([promptPath]),
    ]);
  } catch (removeError) {
    console.log("Note: Error during file removal, will proceed with upload");
  }

  await new Promise((resolve) => setTimeout(resolve, 150));

  // Store name in template file as header
  const templateWithHeader = `TEMPLATE_NAME: ${existingName}\n---\n${templateText}`;

  const [templateResult, promptResult] = await Promise.all([
    supabase.storage
      .from(BUCKET_NAME)
      .upload(
        templatePath,
        new Blob([templateWithHeader], { type: "text/plain" }),
        {
          upsert: true,
          contentType: "text/plain",
        },
      ),
    supabase.storage
      .from(BUCKET_NAME)
      .upload(promptPath, new Blob([promptText], { type: "text/plain" }), {
        upsert: true,
        contentType: "text/plain",
      }),
  ]);

  if (templateResult.error || promptResult.error) {
    throw new Error(
      templateResult.error?.message ||
        promptResult.error?.message ||
        "Upload failed",
    );
  }

  console.log(`✅ formProcessingUtils: Updated template ${templateFileName}`);
  await new Promise((resolve) => setTimeout(resolve, 300));
  return templateFileName;
};

/**
 * Delete a user template by file name
 */
export const deleteUserTemplate = async (patientType, templateFileName) => {
  const userName = await getCurrentUserName();
  if (!userName) throw new Error("No user name available");

  const supabase = await getSupabase();
  const BUCKET_NAME = getBucketName();
  const templateFolderName = getTemplateFolderName(patientType);
  const sanitizedUserName = userName.replace(/[^a-zA-Z0-9@._-]/g, "_");
  const userFolder = `New/templates/${templateFolderName}/${sanitizedUserName}`;

  if (!templateFileName) {
    throw new Error("Template file name is required");
  }

  const templatePath = `${userFolder}/template_${templateFileName}`;
  const promptPath = `${userFolder}/prompt_${templateFileName}`;
  const namePath = `${userFolder}/name_${templateFileName}`;

  // Remove all template files
  const { error } = await supabase.storage
    .from(BUCKET_NAME)
    .remove([templatePath, promptPath, namePath]);

  if (error) {
    console.warn(
      "⚠️ formProcessingUtils: Error deleting template files:",
      error,
    );
    // Continue anyway - template files might not exist
  }

  console.log(`✅ formProcessingUtils: Deleted template ${templateFileName}`);
};

/**
 * Set a template as the default template for the user
 */
export const setDefaultTemplate = async (patientType, templateFileName) => {
  const userName = await getCurrentUserName();
  if (!userName) throw new Error("No user name available");

  const supabase = await getSupabase();
  const BUCKET_NAME = getBucketName();
  const templatePrefix = getTemplateTypePrefix(patientType);
  const templateFolderName = getTemplateFolderName(patientType);
  const sanitizedUserName = userName.replace(/[^a-zA-Z0-9@._-]/g, "_");
  const userFolder = `New/templates/${templateFolderName}/${sanitizedUserName}`;

  if (!templateFileName) {
    throw new Error("Template file name is required");
  }

  const templateNumber = templateFileName;

  // Read the template content to save as default
  const templatePath = `${userFolder}/template_${templateNumber}`;
  const promptPath = `${userFolder}/prompt_${templateNumber}`;
  const defaultTemplatePath = `${userFolder}/default_template_${templatePrefix}`;
  const defaultPromptPath = `${userFolder}/default_prompt_${templatePrefix}`;

  try {
    const [templateResult, promptResult] = await Promise.all([
      supabase.storage.from(BUCKET_NAME).download(templatePath),
      supabase.storage.from(BUCKET_NAME).download(promptPath),
    ]);

    if (templateResult.error || promptResult.error) {
      throw new Error("Failed to read template files");
    }

    const templateText = await templateResult.data.text();
    const promptText = await promptResult.data.text();

    // Remove existing default files first
    await supabase.storage
      .from(BUCKET_NAME)
      .remove([defaultTemplatePath, defaultPromptPath]);

    // Upload as default
    await Promise.all([
      supabase.storage
        .from(BUCKET_NAME)
        .upload(
          defaultTemplatePath,
          new Blob([templateText], { type: "text/plain" }),
          {
            upsert: true,
            contentType: "text/plain",
          },
        ),
      supabase.storage
        .from(BUCKET_NAME)
        .upload(
          defaultPromptPath,
          new Blob([promptText], { type: "text/plain" }),
          {
            upsert: true,
            contentType: "text/plain",
          },
        ),
    ]);

    console.log(
      `✅ formProcessingUtils: Set template ${templatePrefix}${templateNumber} as default`,
    );
  } catch (error) {
    console.error("❌ Error setting default template:", error);
    throw error;
  }
};

/**
 * Save user's personal default template and prompt
 * Also saves the fileName for later retrieval
 */
export const saveUserDefaultTemplate = async (
  patientType,
  templateText,
  promptText,
  templateFileName = null,
) => {
  const userName = await getCurrentUserName();
  if (!userName) throw new Error("No user name available");

  const supabase = await getSupabase();
  const BUCKET_NAME = getBucketName();
  const templatePrefix = getTemplateTypePrefix(patientType);
  const templateFolderName = getTemplateFolderName(patientType);
  const sanitizedUserName = userName.replace(/[^a-zA-Z0-9@._-]/g, "_");
  const userFolder = `New/templates/${templateFolderName}/${sanitizedUserName}`;

  const templatePath = `${userFolder}/default_template_${templatePrefix}`;
  const promptPath = `${userFolder}/default_prompt_${templatePrefix}`;
  // Also save the fileName for reference
  const fileNamePath = `${userFolder}/default_filename_${templatePrefix}`;

  // Remove existing files first
  try {
    await Promise.allSettled([
      supabase.storage.from(BUCKET_NAME).remove([templatePath]),
      supabase.storage.from(BUCKET_NAME).remove([promptPath]),
      supabase.storage.from(BUCKET_NAME).remove([fileNamePath]),
    ]);
  } catch (removeError) {
    console.log("Note: Error during file removal, will proceed with upload");
  }

  await new Promise((resolve) => setTimeout(resolve, 150));

  // Upload template and prompt
  const [templateResult, promptResult] = await Promise.all([
    supabase.storage
      .from(BUCKET_NAME)
      .upload(templatePath, new Blob([templateText], { type: "text/plain" }), {
        upsert: true,
        contentType: "text/plain",
      }),
    supabase.storage
      .from(BUCKET_NAME)
      .upload(promptPath, new Blob([promptText], { type: "text/plain" }), {
        upsert: true,
        contentType: "text/plain",
      }),
  ]);

  if (templateResult.error || promptResult.error) {
    throw new Error(
      templateResult.error?.message ||
        promptResult.error?.message ||
        "Upload failed",
    );
  }

  // If templateFileName provided, save it too
  if (templateFileName) {
    await supabase.storage
      .from(BUCKET_NAME)
      .upload(
        fileNamePath,
        new Blob([templateFileName], { type: "text/plain" }),
        {
          upsert: true,
          contentType: "text/plain",
        },
      );
  }

  console.log(
    `✅ formProcessingUtils: Saved user default template for ${patientType}`,
  );
};

/**
 * Load user's default template file name (if saved)
 */
export const loadUserDefaultTemplateFileName = async (patientType) => {
  try {
    const userName = await getCurrentUserName();
    if (!userName) return null;

    const supabase = await getSupabase();
    const BUCKET_NAME = getBucketName();
    const templatePrefix = getTemplateTypePrefix(patientType);
    const templateFolderName = getTemplateFolderName(patientType);
    const sanitizedUserName = userName.replace(/[^a-zA-Z0-9@._-]/g, "_");
    const userFolder = `New/templates/${templateFolderName}/${sanitizedUserName}`;
    const fileNamePath = `${userFolder}/default_filename_${templatePrefix}`;

    const result = await supabase.storage
      .from(BUCKET_NAME)
      .download(fileNamePath);

    if (!result.error && result.data) {
      const fileName = await result.data.text();
      console.log(
        `✅ formProcessingUtils: Loaded default template file name: ${fileName}`,
      );
      return fileName.trim();
    }
    return null;
  } catch (error) {
    console.warn(
      "⚠️ formProcessingUtils: Error loading default template file name:",
      error,
    );
    return null;
  }
};

/**
 * Clear/Reset the user's default template to revert to the original system default
 */
export const clearDefaultTemplate = async (patientType) => {
  const userName = await getCurrentUserName();
  if (!userName) throw new Error("No user name available");

  const supabase = await getSupabase();
  const BUCKET_NAME = getBucketName();
  const templatePrefix = getTemplateTypePrefix(patientType);
  const templateFolderName = getTemplateFolderName(patientType);
  const sanitizedUserName = userName.replace(/[^a-zA-Z0-9@._-]/g, "_");
  const userFolder = `New/templates/${templateFolderName}/${sanitizedUserName}`;

  const defaultTemplatePath = `${userFolder}/default_template_${templatePrefix}`;
  const defaultPromptPath = `${userFolder}/default_prompt_${templatePrefix}`;

  try {
    // Remove the default template files
    await supabase.storage
      .from(BUCKET_NAME)
      .remove([defaultTemplatePath, defaultPromptPath]);

    console.log(
      `✅ formProcessingUtils: Cleared default template for ${patientType}`,
    );
  } catch (error) {
    console.error("❌ Error clearing default template:", error);
    throw error;
  }
};

/**
 * Load global system template (stored outside user folders for all users)
 * This template will override any hardcoded default templates
 */
export const loadGlobalSystemTemplate = async (patientType) => {
  try {
    const supabase = await getSupabase();
    const BUCKET_NAME = getBucketName();
    const templatePrefix = getTemplateTypePrefix(patientType);
    const templateFolderName = getTemplateFolderName(patientType);

    // Global template is stored in a folder without user name - path: New/templates/{folder}/global/
    const globalFolder = `New/templates/${templateFolderName}/global`;

    const templatePath = `${globalFolder}/default_template_${templatePrefix}`;
    const promptPath = `${globalFolder}/default_prompt_${templatePrefix}`;

    let templateText = null;
    let promptText = null;

    const [templateResult, promptResult] = await Promise.all([
      supabase.storage.from(BUCKET_NAME).download(templatePath),
      supabase.storage.from(BUCKET_NAME).download(promptPath),
    ]);

    if (!templateResult.error && templateResult.data) {
      templateText = await templateResult.data.text();
    }
    if (!promptResult.error && promptResult.data) {
      promptText = await promptResult.data.text();
    }

    if (templateText && promptText) {
      console.log(
        `✅ formProcessingUtils: Loaded global system template for ${patientType}`,
      );
      return { templateText, promptText };
    }

    return { templateText: null, promptText: null };
  } catch (error) {
    console.warn(
      "⚠️ formProcessingUtils: Error loading global system template:",
      error,
    );
    return { templateText: null, promptText: null };
  }
};

/**
 * Save global system template (stored outside user folders for all users)
 * This template will override any hardcoded default templates for all users including new ones
 */
export const saveGlobalSystemTemplate = async (
  patientType,
  templateText,
  promptText,
) => {
  const supabase = await getSupabase();
  const BUCKET_NAME = getBucketName();
  const templatePrefix = getTemplateTypePrefix(patientType);
  const templateFolderName = getTemplateFolderName(patientType);

  // Global template is stored in a folder without user name
  const globalFolder = `New/templates/${templateFolderName}/global`;

  const templatePath = `${globalFolder}/default_template_${templatePrefix}`;
  const promptPath = `${globalFolder}/default_prompt_${templatePrefix}`;

  // Remove existing files first
  try {
    await Promise.allSettled([
      supabase.storage.from(BUCKET_NAME).remove([templatePath]),
      supabase.storage.from(BUCKET_NAME).remove([promptPath]),
    ]);
  } catch (removeError) {
    console.log("Note: Error during file removal, will proceed with upload");
  }

  await new Promise((resolve) => setTimeout(resolve, 150));

  const [templateResult, promptResult] = await Promise.all([
    supabase.storage
      .from(BUCKET_NAME)
      .upload(templatePath, new Blob([templateText], { type: "text/plain" }), {
        upsert: true,
        contentType: "text/plain",
      }),
    supabase.storage
      .from(BUCKET_NAME)
      .upload(promptPath, new Blob([promptText], { type: "text/plain" }), {
        upsert: true,
        contentType: "text/plain",
      }),
  ]);

  if (templateResult.error || promptResult.error) {
    throw new Error(
      templateResult.error?.message ||
        promptResult.error?.message ||
        "Upload failed",
    );
  }

  console.log(
    `✅ formProcessingUtils: Saved global system template for ${patientType}`,
  );
};

/**
 * Check if global system template exists
 */
export const hasGlobalSystemTemplate = async (patientType) => {
  const result = await loadGlobalSystemTemplate(patientType);
  return result.templateText !== null && result.promptText !== null;
};

/**
 * Get the shared templates folder path
 */
const getSharedTemplatesFolder = (patientType) => {
  const templateFolderName = getTemplateFolderName(patientType);
  return `New/templates/${templateFolderName}/shared`;
};

/**
 * Share a template to the shared templates folder
 * Users can share their templates so others can import them
 */
export const shareTemplateToShared = async (
  patientType,
  templateText,
  promptText,
  templateName,
  authorName = null,
) => {
  if (!templateName || !templateName.trim()) {
    throw new Error("Template name is required");
  }

  // Get author name (fallback to current user)
  const author = authorName || (await getCurrentUserName());
  if (!author) {
    throw new Error("Unable to determine template author");
  }

  const supabase = await getSupabase();
  const BUCKET_NAME = getBucketName();
  const sanitizedFileName = sanitizeFileName(templateName.trim());

  if (!sanitizedFileName) {
    throw new Error("Invalid template name");
  }

  const sharedFolder = getSharedTemplatesFolder(patientType);

  // Store name in template file as header with author info
  // Format: "TEMPLATE_NAME: name\nAUTHOR: author\n---\ncontent"
  const templateWithHeader = `TEMPLATE_NAME: ${templateName.trim()}\nAUTHOR: ${author}\n---\n${templateText}`;
  const templatePath = `${sharedFolder}/template_${sanitizedFileName}`;
  const promptPath = `${sharedFolder}/prompt_${sanitizedFileName}`;

  // Check if template with same name already exists in shared folder
  try {
    const existingResult = await supabase.storage
      .from(BUCKET_NAME)
      .download(templatePath);

    if (!existingResult.error && existingResult.data) {
      throw new Error(
        `A shared template named "${templateName.trim()}" already exists. Please use a different name.`,
      );
    }
  } catch (checkError) {
    // If error is not "not found", rethrow
    if (!checkError.message?.includes("already exists")) {
      // Template doesn't exist, which is what we want
    }
  }

  const [templateResult, promptResult] = await Promise.all([
    supabase.storage
      .from(BUCKET_NAME)
      .upload(
        templatePath,
        new Blob([templateWithHeader], { type: "text/plain" }),
        {
          upsert: true,
          contentType: "text/plain",
        },
      ),
    supabase.storage
      .from(BUCKET_NAME)
      .upload(promptPath, new Blob([promptText], { type: "text/plain" }), {
        upsert: true,
        contentType: "text/plain",
      }),
  ]);

  if (templateResult.error || promptResult.error) {
    throw new Error(
      templateResult.error?.message ||
        promptResult.error?.message ||
        "Failed to share template",
    );
  }

  console.log(
    `✅ formProcessingUtils: Shared template "${templateName.trim()}" to shared templates`,
  );
  return { name: templateName.trim(), fileName: sanitizedFileName, author };
};

/**
 * Load all shared templates from the shared folder
 */
export const loadSharedTemplates = async (patientType) => {
  try {
    const supabase = await getSupabase();
    const BUCKET_NAME = getBucketName();
    const sharedFolder = getSharedTemplatesFolder(patientType);

    // List all files in shared folder
    const { data: files, error } = await supabase.storage
      .from(BUCKET_NAME)
      .list(sharedFolder);

    if (error) {
      console.warn(
        "⚠️ formProcessingUtils: Error listing shared templates:",
        error,
      );
      return [];
    }

    // Filter for template files
    const templateFiles = (files || [])
      .filter(
        (file) =>
          file.name.startsWith("template_") && !file.name.startsWith("prompt_"),
      )
      .map((file) => file.name.replace(/^template_/, ""))
      .filter((name) => name.length > 0);

    // Load each shared template
    const sharedTemplates = [];
    for (const fileName of templateFiles) {
      try {
        const templatePath = `${sharedFolder}/template_${fileName}`;
        const promptPath = `${sharedFolder}/prompt_${fileName}`;

        const [templateResult, promptResult] = await Promise.all([
          supabase.storage.from(BUCKET_NAME).download(templatePath),
          supabase.storage.from(BUCKET_NAME).download(promptPath),
        ]);

        if (!templateResult.error && !promptResult.error) {
          const templateContent = await templateResult.data.text();
          const promptText = await promptResult.data.text();

          // Extract name and author from template file header
          let templateName = fileName;
          let authorName = "Unknown";
          let templateText = templateContent;

          if (templateContent.startsWith("TEMPLATE_NAME:")) {
            const headerEnd = templateContent.indexOf("\n---");
            if (headerEnd !== -1) {
              const headerContent = templateContent.substring(0, headerEnd);

              // Extract template name
              const nameMatch = headerContent.match(/TEMPLATE_NAME:\s*(.+)/);
              if (nameMatch) {
                templateName = nameMatch[1].trim();
              }

              // Extract author
              const authorMatch = headerContent.match(/AUTHOR:\s*(.+)/);
              if (authorMatch) {
                authorName = authorMatch[1].trim();
              }

              // Get actual template text (after ---)
              templateText = templateContent.substring(headerEnd + 4).trim();
              if (templateText.startsWith("\n")) {
                templateText = templateText.substring(1);
              }
            }
          }

          sharedTemplates.push({
            fileName: fileName,
            template: templateText,
            prompt: promptText,
            name: templateName,
            author: authorName,
          });
        }
      } catch (error) {
        console.warn(
          `⚠️ formProcessingUtils: Error loading shared template ${fileName}:`,
          error,
        );
      }
    }

    console.log(
      `✅ formProcessingUtils: Loaded ${sharedTemplates.length} shared templates for ${patientType}`,
    );
    return sharedTemplates;
  } catch (error) {
    console.error(
      "❌ formProcessingUtils: Error loading shared templates:",
      error,
    );
    return [];
  }
};

/**
 * Import a shared template from the shared folder to user's folder
 */
export const importSharedTemplate = async (
  patientType,
  sharedTemplateFileName,
  newTemplateName = null,
) => {
  const userName = await getCurrentUserName();
  if (!userName) {
    throw new Error("No user name available");
  }

  const supabase = await getSupabase();
  const BUCKET_NAME = getBucketName();
  const templateFolderName = getTemplateFolderName(patientType);
  const sanitizedUserName = userName.replace(/[^a-zA-Z0-9@._-]/g, "_");
  const userFolder = `New/templates/${templateFolderName}/${sanitizedUserName}`;

  // Load the shared template first
  const sharedFolder = getSharedTemplatesFolder(patientType);
  const templatePath = `${sharedFolder}/template_${sharedTemplateFileName}`;
  const promptPath = `${sharedFolder}/prompt_${sharedTemplateFileName}`;

  const [templateResult, promptResult] = await Promise.all([
    supabase.storage.from(BUCKET_NAME).download(templatePath),
    supabase.storage.from(BUCKET_NAME).download(promptPath),
  ]);

  if (templateResult.error || promptResult.error) {
    throw new Error("Failed to read shared template");
  }

  const templateContent = await templateResult.data.text();
  const promptText = await promptResult.data.text();

  // Extract name and author from shared template
  let templateName = sharedTemplateFileName;
  let authorName = "Unknown";
  let templateText = templateContent;

  if (templateContent.startsWith("TEMPLATE_NAME:")) {
    const headerEnd = templateContent.indexOf("\n---");
    if (headerEnd !== -1) {
      const headerContent = templateContent.substring(0, headerEnd);

      const nameMatch = headerContent.match(/TEMPLATE_NAME:\s*(.+)/);
      if (nameMatch) {
        templateName = nameMatch[1].trim();
      }

      const authorMatch = headerContent.match(/AUTHOR:\s*(.+)/);
      if (authorMatch) {
        authorName = authorMatch[1].trim();
      }

      templateText = templateContent.substring(headerEnd + 4).trim();
      if (templateText.startsWith("\n")) {
        templateText = templateText.substring(1);
      }
    }
  }

  // Use custom name or add "(imported)" suffix
  const finalName =
    newTemplateName && newTemplateName.trim()
      ? newTemplateName.trim()
      : `${templateName} (imported)`;

  const sanitizedFileName = sanitizeFileName(finalName);
  if (!sanitizedFileName) {
    throw new Error("Invalid template name");
  }

  // Check if template with this name already exists in user's folder
  const userTemplates = await loadSavedUserTemplates(patientType);
  const existingTemplate = userTemplates.find((t) => t.name === finalName);

  if (existingTemplate) {
    throw new Error(
      `You already have a template named "${finalName}". Please use a different name.`,
    );
  }

  // Save to user's folder with author info in header
  const templateWithHeader = `TEMPLATE_NAME: ${finalName}\nAUTHOR: ${authorName}\nIMPORTED_FROM: shared\n---\n${templateText}`;
  const userTemplatePath = `${userFolder}/template_${sanitizedFileName}`;
  const userPromptPath = `${userFolder}/prompt_${sanitizedFileName}`;

  const [uploadTemplateResult, uploadPromptResult] = await Promise.all([
    supabase.storage
      .from(BUCKET_NAME)
      .upload(
        userTemplatePath,
        new Blob([templateWithHeader], { type: "text/plain" }),
        {
          upsert: true,
          contentType: "text/plain",
        },
      ),
    supabase.storage
      .from(BUCKET_NAME)
      .upload(userPromptPath, new Blob([promptText], { type: "text/plain" }), {
        upsert: true,
        contentType: "text/plain",
      }),
  ]);

  if (uploadTemplateResult.error || uploadPromptResult.error) {
    throw new Error(
      uploadTemplateResult.error?.message ||
        uploadPromptResult.error?.message ||
        "Failed to import template",
    );
  }

  console.log(
    `✅ formProcessingUtils: Imported shared template "${finalName}" from ${authorName}`,
  );
  return { name: finalName, fileName: sanitizedFileName, author: authorName };
};

/**
 * Remove a template from the shared folder
 */
export const removeSharedTemplate = async (patientType, templateFileName) => {
  const supabase = await getSupabase();
  const BUCKET_NAME = getBucketName();
  const sharedFolder = getSharedTemplatesFolder(patientType);

  const templatePath = `${sharedFolder}/template_${templateFileName}`;
  const promptPath = `${sharedFolder}/prompt_${templateFileName}`;

  const { error } = await supabase.storage
    .from(BUCKET_NAME)
    .remove([templatePath, promptPath]);

  if (error) {
    throw new Error("Failed to remove shared template");
  }

  console.log(
    `✅ formProcessingUtils: Removed shared template ${templateFileName}`,
  );
};
